<template>
  <Listbox
    v-slot="{ open }"
    v-model="selectedItem"
    as="div"
    :multiple="multiple"
    :disabled="disabled"
  >
    <ListboxLabel v-if="label" class="mb-1 inline-block">
      <slot name="label">
        <span class="font-medium" :class="{ 'opacity-50': disabled }">
          {{ label }}
        </span>
      </slot>
    </ListboxLabel>
    <Float
      as="div"
      placement="bottom-start"
      :floating-as="floatingAs"
      class="relative"
      flip
      adaptive-width
      :offset="4"
    >
      <ListboxButton as="template">
        <slot name="activator" :selected-item="selectedItem" :open="open">
          <button
            :class="[
              errorMessage
                ? '!border-error'
                : 'border-border-primary hover:border-border-hover focus:border-text-primary',
              disabled ? 'opacity-50' : '',
              big ? 'h-12.5' : 'h-10',
            ]"
            class="relative flex w-full items-center border bg-white px-3 py-1.5 text-left font-normal text-text-primary focus:outline-none"
            :data-e2e-dropdown="dataE2eDropdown"
          >
            <span v-if="hasValues && selectedItem" class="flex-auto truncate">
              {{
                Array.isArray(selectedItem)
                  ? selectedItem.map((item) => item?.[titleKey]).join(", ")
                  : selectedItem[titleKey]
              }}
            </span>
            <span
              v-else
              class="flex-auto select-none truncate text-gray-elements"
              :class="[big ? '' : 'text-opacity-50']"
            >
              {{ placeholder }}
            </span>

            <UiIcon
              :name="open ? 'expand_less' : 'expand_more'"
              class="pointer-events-none h-5 w-5 flex-shrink-0 text-primary"
              :class="[big ? '' : '']"
            />
          </button>
        </slot>
      </ListboxButton>

      <ListboxOptions
        class="max-h-60w-full transform-gpu space-y-3.5 overflow-auto border border-gray-60 bg-white p-3.5 focus:outline-none md:min-w-min"
        :class="dropdownClasses"
      >
        <ListboxOption
          v-for="item in items"
          v-slot="{ active, selected, disabled }"
          :key="item.value"
          :value="item"
          :disabled="item.disabled"
          as="template"
        >
          <li
            class=""
            :class="
              cn('relative cursor-pointer select-none text-primary', [
                { 'cursor-default opacity-50': disabled },
                itemClasses,
              ])
            "
            :title="item.tooltip || ''"
            :data-e2e-item="dataE2eItem"
          >
            <span class="block truncate font-normal">
              <slot
                name="item"
                :item="item"
                :active="active"
                :selected="selected"
                :disabled="disabled"
              >
                {{ item[titleKey] }}
              </slot>
            </span>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </Float>
    <FieldHint
      v-if="!hideDetails"
      :error="Boolean(errorMessage)"
      :message="errorMessage || hint"
    />
  </Listbox>
</template>

<script setup lang="ts" generic="T extends { [key: string]: any }">
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  provideUseId,
  ListboxLabel,
} from "@headlessui/vue";
import { Float } from "@headlessui-float/vue";
import { useField } from "vee-validate";
import FieldHint from "~/components/shared/FieldHint.vue";
import { UiSelectModelValue } from "~/types/components.types";

const props = withDefaults(
  defineProps<{
    modelValue: any;
    name: string;
    items: T[];
    placeholder?: string;
    hint?: string;
    label?: string;
    hideDetails?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    titleKey?: string;
    valueKey?: string;
    updateInitValue?: boolean;
    big?: boolean;
    itemClasses?: string;
    floatingAs?: string;
    dropdownClasses?: string;
    dataE2eDropdown?: string;
    dataE2eItem?: string;
  }>(),
  {
    placeholder: "",
    hint: "",
    label: "",
    titleKey: "label",
    valueKey: "value",
    floatingAs: "div",
    itemClasses: "",
    dropdownClasses: "",
  },
);

provideUseId(() => useId());
// provideUseId(() => props.name);

const emit = defineEmits<{
  (e: "update:modelValue", selectedValue: UiSelectModelValue<T>): void;
}>();

const { errorMessage, handleChange, meta } = useField(props.name, undefined, {
  initialValue: props.modelValue,
});

const selectedItem = computed({
  get() {
    if (isArray(props.modelValue)) {
      return props.modelValue.map((value) => {
        return findCurrentSelection(value);
      });
    } else {
      return findCurrentSelection(props.modelValue);
    }
  },
  set(newValue) {
    if (props.name) {
      // handleChange(props.modelValue);
      handleChange(newValue);
    }

    emit("update:modelValue", newValue);
  },
});

onMounted(() => {
  if (props.modelValue) {
    if (props.name) {
      handleChange(findCurrentSelection(props.modelValue));
    }

    // emit("update:modelValue", findCurrentSelection(props.modelValue));
  }
});

function findCurrentSelection(value: Nullable<T | ValueOf<keyof T>>) {
  if (!value) return null;
  if (typeof value !== "object") {
    return props.items.find((item) => {
      return item[props.valueKey] === value;
    });
  } else {
    return props.items.find((item) => {
      return item[props.valueKey] === value[props.valueKey];
    });
  }
}
function setCurrentSelection() {
  if (Array.isArray(props.modelValue)) {
    const currentModelValue = props.modelValue.map((value) => {
      return findCurrentSelection(value);
    });
    emit("update:modelValue", currentModelValue);
    return currentModelValue;
  } else {
    const currentModelValue = findCurrentSelection(props.modelValue);

    emit("update:modelValue", currentModelValue);
    return currentModelValue;
  }
}

if (props.updateInitValue) {
  setCurrentSelection();
}

const hasValues = computed(() => {
  return Boolean(
    isArray(selectedItem.value)
      ? selectedItem.value.length
      : selectedItem.value,
  );
});

defineExpose({
  meta,
  value: props.modelValue,
  name: props.name,
});
</script>
